/**
 * BrandVoiceDialog — Opens from CompaniesPage card; admin uploads 3-15 example posts,
 * backend learns voice profile via GPT-5.2 and persists on company.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Wand2, Plus, Trash2, Sparkles, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrandVoiceDialog({ companyId, companyName, open, onOpenChange }) {
  const [examples, setExamples] = useState(["", "", ""]);
  const [profile, setProfile] = useState(null);
  const [count, setCount] = useState(0);
  const [trainedAt, setTrainedAt] = useState(null);
  const [training, setTraining] = useState(false);
  const [draftUrl, setDraftUrl] = useState("");
  const [draftBrief, setDraftBrief] = useState("");
  const [drafting, setDrafting] = useState(false);

  useEffect(() => {
    if (!open || !companyId) return;
    axios.get(`${API}/companies/${companyId}/brand-voice`, { withCredentials: true })
      .then((r) => {
        setProfile(r.data.voice_profile || null);
        setCount(r.data.voice_examples_count || 0);
        setTrainedAt(r.data.voice_trained_at || null);
      })
      .catch(console.error);
  }, [open, companyId]);

  const addField = () => setExamples((p) => [...p, ""]);
  const updField = (i, v) => setExamples((p) => p.map((e, idx) => idx === i ? v : e));
  const rmField = (i) => setExamples((p) => p.filter((_, idx) => idx !== i));

  const train = async () => {
    const clean = examples.map((e) => e.trim()).filter((e) => e.length > 0);
    if (clean.length < 3) {
      toast.info("Bitte mindestens 3 Beispiel-Posts angeben");
      return;
    }
    setTraining(true);
    try {
      const res = await axios.post(
        `${API}/companies/${companyId}/brand-voice/train`,
        { examples: clean },
        { withCredentials: true }
      );
      setProfile(res.data.voice_profile);
      setCount(res.data.voice_examples_count);
      setTrainedAt(res.data.voice_trained_at);
      toast.success("Brand Voice trainiert!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Training fehlgeschlagen");
    }
    setTraining(false);
  };

  const draftFromAi = async () => {
    if (!draftUrl.trim() && !draftBrief.trim()) {
      toast.error("Bitte URL oder Brief eingeben");
      return;
    }
    setDrafting(true);
    try {
      const res = await axios.post(
        `${API}/companies/${companyId}/brand-voice/draft`,
        { url: draftUrl.trim() || undefined, brief: draftBrief.trim() || undefined, count: 8 },
        { withCredentials: true }
      );
      const drafted = res.data?.examples || [];
      if (drafted.length === 0) {
        toast.warning("Keine Beispiele erhalten");
        return;
      }
      // Merge: fill empty slots first, then append remaining
      setExamples((prev) => {
        const next = [...prev];
        let di = 0;
        for (let i = 0; i < next.length && di < drafted.length; i++) {
          if (!next[i].trim()) { next[i] = drafted[di++]; }
        }
        while (di < drafted.length && next.length < 15) {
          next.push(drafted[di++]);
        }
        return next;
      });
      toast.success(`${drafted.length} KI-Beispiele eingefuegt — bitte pruefen, dann trainieren`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "KI-Entwurf fehlgeschlagen");
    }
    setDrafting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="brand-voice-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#2563EB]" /> Brand Voice Training
            <span className="text-xs font-normal text-[var(--studio-text-muted)]">— {companyName}</span>
          </DialogTitle>
        </DialogHeader>

        {profile && (
          <div className="bg-[var(--studio-bg)] rounded-lg p-4 space-y-2" data-testid="brand-voice-profile">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Aktuelles Voice-Profil</p>
              <Badge variant="secondary" className="text-[10px]">
                {count} Beispiele · {trainedAt ? new Date(trainedAt).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"}
              </Badge>
            </div>
            {profile.tone && <p className="text-xs"><b>Ton:</b> {profile.tone}</p>}
            {profile.persona_summary && <p className="text-xs"><b>Persona:</b> {profile.persona_summary}</p>}
            {profile.emoji_policy && <p className="text-xs"><b>Emoji-Policy:</b> {profile.emoji_policy}</p>}
            {profile.do?.length > 0 && (
              <p className="text-xs"><b>✓ Tun:</b> {profile.do.join(" · ")}</p>
            )}
            {profile.dont?.length > 0 && (
              <p className="text-xs"><b>✗ Vermeiden:</b> {profile.dont.join(" · ")}</p>
            )}
          </div>
        )}

        <div className="space-y-3">
          <div
            className="p-3 rounded-lg border border-[#2563EB]/30 bg-gradient-to-br from-[#2563EB]/5 to-transparent space-y-2"
            data-testid="voice-draft-panel"
          >
            <div className="flex items-center gap-2 text-xs font-medium text-[#2563EB]">
              <Sparkles className="w-3.5 h-3.5" />
              KI-Entwurf — generiert 5-10 Beispiel-Posts aus Website + Brief
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
              <div className="relative">
                <Globe className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--studio-text-muted)]" />
                <Input
                  value={draftUrl}
                  onChange={(e) => setDraftUrl(e.target.value)}
                  placeholder="https://www.firmen-website.ch (optional)"
                  className="pl-7 text-sm h-9"
                  data-testid="voice-draft-url"
                />
              </div>
              <Button
                type="button"
                onClick={draftFromAi}
                disabled={drafting || (!draftUrl.trim() && !draftBrief.trim())}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-9 text-xs"
                data-testid="voice-draft-btn"
              >
                {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                Beispiele generieren
              </Button>
            </div>
            <Textarea
              value={draftBrief}
              onChange={(e) => setDraftBrief(e.target.value)}
              placeholder="Optional: zusaetzlicher Brief (z.B. 'aktuelle Kampagne: Neueroeffnung Studio Zuerich-West')"
              rows={2}
              className="text-xs"
              data-testid="voice-draft-brief"
            />
            <p className="text-[10px] text-[var(--studio-text-muted)]">
              Tipp: Die KI nutzt das Marken-Profil dieser Firma + Website/Brief und befuellt freie Felder unten.
            </p>
          </div>

          <p className="text-sm font-medium">Beispiel-Posts (3-15)</p>
          <p className="text-xs text-[var(--studio-text-muted)]">
            Fuege Posts ein, die deinen Tonfall optimal widerspiegeln. Die KI lernt daraus Tonalitaet, Vokabular,
            Satzbau, Do's & Don'ts.
          </p>
          {examples.map((e, i) => (
            <div key={i} className="flex gap-2">
              <Textarea
                value={e}
                onChange={(ev) => updField(i, ev.target.value)}
                placeholder={`Post ${i + 1}…`}
                rows={2}
                className="flex-1"
                data-testid={`voice-example-${i}`}
              />
              {examples.length > 3 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => rmField(i)}
                  className="text-red-500"
                  data-testid={`remove-example-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          {examples.length < 15 && (
            <Button variant="outline" size="sm" onClick={addField} data-testid="add-example-btn">
              <Plus className="w-3 h-3 mr-1" /> Weiteres Beispiel
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schliessen</Button>
          <Button
            onClick={train}
            disabled={training}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="train-voice-btn"
          >
            {training ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {profile ? "Erneut trainieren" : "Voice trainieren"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
